import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, className = '', y = 28 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-70px' }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const LineReveal = ({ lines, className = '', as: Tag = 'h1' }) => (
  <Tag className={className}>
    {lines.map((l, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
          className="block will-change-transform"
          initial={{ y: '115%' }}
          animate={{ y: '0%' }}
          transition={{ duration: 1, delay: 0.2 + i * 0.13, ease: EASE }}
        >
          {l}
        </motion.span>
      </span>
    ))}
  </Tag>
);

export const Overline = ({ children, className = '' }) => (
  <p className={`text-xs font-bold tracking-[0.25em] uppercase text-volt ${className}`}>
    <span className="inline-block w-8 h-px bg-volt align-middle mr-3" aria-hidden="true" />
    {children}
  </p>
);

export const SectionHead = ({ overline, title, className = '' }) => (
  <Reveal className={className}>
    <Overline>{overline}</Overline>
    {title && (
      <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.05] text-white max-w-3xl">
        {title}
      </h2>
    )}
  </Reveal>
);

export const VoltButton = ({ to, href, children, variant = 'solid', testId, onClick, type }) => {
  const base =
    'group inline-flex items-center gap-3 font-mono text-xs font-bold uppercase tracking-[0.2em] px-7 py-4 transition-[background-color,color,border-color,transform] duration-300 active:scale-[0.97]';
  const styles =
    variant === 'solid'
      ? 'bg-volt text-black hover:bg-volt-hover'
      : 'border border-white/25 text-white hover:border-volt hover:text-volt';
  const inner = (
    <>
      <span>{children}</span>
      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </>
  );
  if (to) return <Link to={to} data-testid={testId} className={`${base} ${styles}`}>{inner}</Link>;
  if (href) return <a href={href} data-testid={testId} className={`${base} ${styles}`} onClick={onClick}>{inner}</a>;
  return <button type={type || 'button'} onClick={onClick} data-testid={testId} className={`${base} ${styles}`}>{inner}</button>;
};

export const Page = ({ meta, children }) => {
  useEffect(() => {
    document.title = meta.title;
    document.documentElement.lang = meta.lang;
    let tag = document.querySelector('meta[name="description"]');
    if (tag) tag.setAttribute('content', meta.desc);
    window.scrollTo(0, 0);
  }, [meta]);
  return <>{children}</>;
};

export const HeroShell = ({ overline, lines, sub, image, children, testId }) => (
  <section data-testid={testId} className="relative overflow-hidden border-b border-white/10 blueprint">
    <div className="absolute inset-0 spotlight" aria-hidden="true" />
    {image && (
      <div className="absolute inset-0" aria-hidden="true">
        <img src={image} alt="" className="w-full h-full object-cover opacity-[0.16] grayscale" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/40 to-obsidian" />
      </div>
    )}
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-40 md:pt-52 pb-20 md:pb-28">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.1 }}
      >
        <Overline>{overline}</Overline>
      </motion.div>
      <LineReveal
        lines={lines}
        className="mt-8 font-display font-black uppercase tracking-tighter leading-[0.95] text-white text-[clamp(2.6rem,7.5vw,6.5rem)]"
      />
      {sub && (
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
          className="mt-8 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed"
        >
          {sub}
        </motion.p>
      )}
      {children && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          {children}
        </motion.div>
      )}
    </div>
  </section>
);
